import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertDialogModal, Typography } from "@reconvert/react-ui-component";
import { useAppSelector } from "../app/hooks";
import { selectAppState } from "../app/slice/appSlice";
import {
  IFramePostMessageType,
  IFramePostMessageEvent,
  sendPostMessage,
} from "../utils/helpers";
import { useDirection } from "../hooks/useDirection";

export interface OpenConfirmModalConfig {
  title: string;
  okButtonText: string;
  cancelButtonText: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  destructive?: boolean;
  loading?: boolean;
  /** Optional body content (e.g. warning message) shown below the title. Sent in postMessage so parent modal can show it too. */
  content?: string;
}

interface PendingRequest {
  onConfirm: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
}

interface ConfirmModalContextValue {
  openConfirmModal: (config: OpenConfirmModalConfig) => void;
  /** Update loading state for the currently open local confirm modal (e.g. while async onConfirm runs). No-op when using parent modal. */
  setConfirmLoading: (loading: boolean) => void;
}

const ConfirmModalContext = React.createContext<ConfirmModalContextValue | null>(null);

const defaultLocalState = {
  isOpen: false,
  title: "",
  okButtonText: "",
  cancelButtonText: "",
  onConfirm: (() => {}) as () => void | Promise<void>,
  onCancel: () => {},
  destructive: false,
  loading: false,
  content: "" as string,
};

export function ConfirmModalProvider({ children }: { children: React.ReactNode }) {
  const { isAppBridgePresent } = useAppSelector(selectAppState);
  const { direction } = useDirection();
  const pendingRequestsRef = useRef<Map<string, PendingRequest>>(new Map());
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [localState, setLocalState] = useState({
    ...defaultLocalState,
  });
  const [confirmLoading, setConfirmLoadingState] = useState(false);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === IFramePostMessageEvent.RUN_CONFIRM) {
        const { requestId } = event.data as { requestId: string };
        const pending = pendingRequestsRef.current.get(requestId);

        if (!pending) return;

        pendingRequestsRef.current.delete(requestId);
        try {
          await pending.onConfirm();
        } finally {
          sendPostMessage(IFramePostMessageType.CONFIRM_FINISHED, {
            type: IFramePostMessageType.CONFIRM_FINISHED,
            requestId,
          });
        }
        return;
      }

      if (event.data?.type === IFramePostMessageEvent.OPEN_CONFIRM_MODAL_RESPONSE) {
        const { requestId, action } = event.data as { requestId: string; action: "confirm" | "cancel" };
        const pending = pendingRequestsRef.current.get(requestId);

        if (!pending) return;

        pendingRequestsRef.current.delete(requestId);
        if (action === "cancel") {
          await pending.onCancel();
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const openConfirmModal = useCallback(
    (config: OpenConfirmModalConfig) => {
      if (isAppBridgePresent) {
        const requestId = crypto.randomUUID();

        pendingRequestsRef.current.set(requestId, {
          onConfirm: config.onConfirm,
          onCancel: config.onCancel,
        });
        sendPostMessage(IFramePostMessageType.OPEN_CONFIRM_MODAL, {
          requestId,
          title: config.title,
          okButtonText: config.okButtonText,
          cancelButtonText: config.cancelButtonText,
          destructive: config.destructive,
          content: config.content ?? "",
        });
      } else {
        setConfirmLoadingState(false);
        setLocalState({
          isOpen: true,
          title: config.title,
          okButtonText: config.okButtonText,
          cancelButtonText: config.cancelButtonText,
          onConfirm: config.onConfirm,
          onCancel: config.onCancel,
          destructive: config.destructive ?? false,
          loading: config.loading ?? false,
          content: config.content ?? "",
        });
      }
    },
    [isAppBridgePresent]
  );

  const closeLocalModal = useCallback(() => {
    setLocalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const setConfirmLoading = useCallback((loading: boolean) => {
    setConfirmLoadingState(loading);
  }, []);

  const handleLocalConfirm = useCallback(async () => {
    setConfirmLoadingState(true);
    try {
      await localState.onConfirm();
    } finally {
      setConfirmLoadingState(false);
      closeLocalModal();
    }
  }, [localState.onConfirm, closeLocalModal]);

  const handleLocalCancel = useCallback(() => {
    localState.onCancel();
    closeLocalModal();
  }, [localState.onCancel, closeLocalModal]);

  return (
    <ConfirmModalContext.Provider value={{ openConfirmModal, setConfirmLoading }}>
      {children}
      {!isAppBridgePresent && localState.isOpen && (
        <AlertDialogModal
          title={localState.title}
          okButtonText={localState.okButtonText}
          cancelButtonText={localState.cancelButtonText}
          size="2xl"
          isOpen={localState.isOpen}
          leastDestructiveRef={cancelRef}
          onClose={handleLocalCancel}
          okButtonOnClick={handleLocalConfirm}
          cancelButtonOnClick={handleLocalCancel}
          confirmLoading={localState.loading || confirmLoading}
          direction={direction}
        >
          {localState.content ? (
            <Typography variant="body-16-regular">{localState.content}</Typography>
          ) : null}
        </AlertDialogModal>
      )}
    </ConfirmModalContext.Provider>
  );
}

export function useCentralConfirmModal(): ConfirmModalContextValue {
  const ctx = React.useContext(ConfirmModalContext);

  if (!ctx) {
    throw new Error("useCentralConfirmModal must be used within ConfirmModalProvider");
  }

  return ctx;
}
